import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Alert, Avatar, Card, Empty, Skeleton, Space, Statistic, Table, Tag, Typography } from 'antd';
import type {
  StudentAttendanceEntry, StudentDocumentRow, StudentFeeInvoice, StudentMarkRow, StudentTimetableSlot,
} from '@velora/contracts';
import { productApi } from '../../api';

const DAY_LABELS = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function EmptyState() {
  return (
    <Alert
      type="warning"
      showIcon
      message="Your login isn't linked to a student record"
      description="Ask the admin to set person_id on your student record (email must match your login)."
    />
  );
}

export function StudentHomePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const q = useQuery({
    queryKey: ['student-home', institution],
    queryFn: () => productApi.getStudentHome(institution),
    enabled: !!institution,
  });
  if (q.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!q.data) return <Alert type="error" showIcon message="Could not load" />;
  if (!q.data.student) return <EmptyState />;
  const s = q.data.student;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card size="small">
        <Space size={12} align="center">
          <Avatar size={54} style={{ background: `hsl(${(s.name.length * 37) % 360}, 40%, 60%)`, fontSize: 18 }}>
            {s.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
          </Avatar>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700 }}>{s.name}</div>
            <div style={{ color: '#5b6478', fontSize: 12 }}>
              {s.grade}{s.section ? `-${s.section}` : ''} · <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{s.admission_number}</span>
            </div>
            <div style={{ color: '#5b6478', fontSize: 11.5 }}>{s.institution_name}</div>
          </div>
        </Space>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small">
          <Statistic title="Attendance" value={q.data.attendance_percent !== null ? `${q.data.attendance_percent}%` : '—'} valueStyle={{ color: q.data.attendance_percent !== null && q.data.attendance_percent < 85 ? '#b3382f' : '#14213d', fontSize: 20 }} />
        </Card>
        <Card size="small">
          <Statistic title="Fees due" value={`₹${paise(q.data.fees_open_paise)}`} valueStyle={{ color: q.data.fees_open_paise > 0 ? '#b3382f' : '#1c6442', fontSize: 20 }} />
        </Card>
        <Card size="small">
          <Statistic title="Next exam" value={q.data.next_exam?.name ?? '—'} valueStyle={{ fontSize: 15 }} />
          {q.data.next_exam?.date && <Typography.Text type="secondary" style={{ fontSize: 11 }}>{q.data.next_exam.date}</Typography.Text>}
        </Card>
      </div>

      <Card size="small" title="Today">
        {q.data.today_slots.length === 0 ? (
          <Empty description="No periods today. Enjoy!" style={{ padding: 20 }} />
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {q.data.today_slots.map((s) => <SlotRow key={`${s.day_of_week}-${s.period}`} slot={s} />)}
          </div>
        )}
      </Card>
    </Space>
  );
}

function SlotRow({ slot }: { slot: StudentTimetableSlot }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ width: 40, height: 40, borderRadius: 8, background: '#eaf3fb', color: '#2f6ab0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>P{slot.period}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{slot.subject_name}</div>
        <div style={{ color: '#5b6478', fontSize: 12 }}>{slot.subject_code}{slot.teacher_name ? ` · ${slot.teacher_name}` : ''}</div>
      </div>
    </div>
  );
}

export function StudentTimetablePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const q = useQuery({
    queryKey: ['student-timetable', institution],
    queryFn: () => productApi.getStudentTimetable(institution),
    enabled: !!institution,
  });
  if (q.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!q.data?.student) return <EmptyState />;
  const byDay = new Map<number, StudentTimetableSlot[]>();
  for (const s of q.data.slots) {
    const arr = byDay.get(s.day_of_week) ?? [];
    arr.push(s);
    byDay.set(s.day_of_week, arr);
  }
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>My timetable</Typography.Title>
      {q.data.slots.length === 0 ? (
        <Empty description="No timetable slots yet for your section." style={{ padding: 20 }} />
      ) : (
        [1, 2, 3, 4, 5, 6].map((d) => {
          const rows = byDay.get(d) ?? [];
          if (rows.length === 0) return null;
          return (
            <Card key={d} size="small" title={DAY_LABELS[d]}>
              {rows.sort((a, b) => a.period - b.period).map((s) => (
                <div key={`${d}-${s.period}`} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderTop: '1px solid #f1f1f4' }}>
                  <Tag>P{s.period}</Tag>
                  <div style={{ flex: 1 }}>
                    <b style={{ fontSize: 13 }}>{s.subject_name}</b>
                    <div style={{ fontSize: 11.5, color: '#5b6478' }}>{s.teacher_name ?? 'no teacher'}</div>
                  </div>
                </div>
              ))}
            </Card>
          );
        })
      )}
    </Space>
  );
}

export function StudentMarksPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const q = useQuery({
    queryKey: ['student-marks', institution],
    queryFn: () => productApi.getStudentMarks(institution),
    enabled: !!institution,
  });
  if (q.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!q.data?.student) return <EmptyState />;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>My marks</Typography.Title>
      {q.data.rows.length === 0 ? (
        <Empty description="No marks published yet." style={{ padding: 20 }} />
      ) : (
        <Card size="small">
          <Table<StudentMarkRow>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey={(r) => `${r.exam_name}-${r.subject_code}-${r.date}`}
            dataSource={q.data.rows}
            columns={[
              { title: 'Exam', dataIndex: 'exam_name', key: 'e' },
              { title: 'Subject', key: 's', render: (_, r) => <><b>{r.subject_name}</b> <Typography.Text type="secondary" style={{ fontSize: 11 }}>· {r.subject_code}</Typography.Text></> },
              { title: 'Date', dataIndex: 'date', key: 'd', width: 100 },
              { title: 'Marks', key: 'm', width: 100, align: 'right', render: (_, r) => r.absent ? <Typography.Text type="secondary">absent</Typography.Text> : (r.marks_obtained ?? <Typography.Text type="secondary">—</Typography.Text>) },
              { title: 'Max', dataIndex: 'max_marks', key: 'mm', width: 60, align: 'right' },
              {
                title: 'Result', key: 'r', width: 80,
                render: (_, r) => {
                  if (r.result === 'pass') return <Tag color="success">pass</Tag>;
                  if (r.result === 'fail') return <Tag color="error">fail</Tag>;
                  if (r.result === 'absent') return <Tag>abs</Tag>;
                  return <Tag>—</Tag>;
                },
              },
            ]}
          />
        </Card>
      )}
    </Space>
  );
}

export function StudentAttendancePage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const q = useQuery({
    queryKey: ['student-attendance', institution],
    queryFn: () => productApi.getStudentAttendance(institution),
    enabled: !!institution,
  });
  if (q.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!q.data?.student) return <EmptyState />;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>My attendance</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          {q.data.percent !== null ? `${q.data.percent}% present` : 'no marks yet'} · {q.data.entries.length} days ({q.data.from} → {q.data.to})
        </Typography.Text>
      </div>
      {q.data.entries.length === 0 ? (
        <Empty description="No attendance marked yet." style={{ padding: 20 }} />
      ) : (
        <Card size="small">
          <Table<StudentAttendanceEntry>
            size="small"
            pagination={{ pageSize: 15 }}
            rowKey={(r) => r.date}
            dataSource={q.data.entries}
            columns={[
              { title: 'Date', dataIndex: 'date', key: 'd', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              {
                title: 'Status', dataIndex: 'status', key: 's', width: 100,
                render: (v: string) => <Tag color={v === 'present' ? 'success' : v === 'absent' ? 'error' : 'warning'}>{v}</Tag>,
              },
              { title: 'Note', dataIndex: 'note', key: 'n', render: (v: string | null) => v ?? <Typography.Text type="secondary">—</Typography.Text> },
            ]}
          />
        </Card>
      )}
    </Space>
  );
}

export function StudentFeesPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const q = useQuery({
    queryKey: ['student-fees', institution],
    queryFn: () => productApi.getStudentFees(institution),
    enabled: !!institution,
  });
  if (q.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!q.data?.student) return <EmptyState />;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>My fees</Typography.Title>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Billed" value={`₹${paise(q.data.totals.billed)}`} valueStyle={{ fontSize: 18 }} /></Card>
        <Card size="small"><Statistic title="Paid" value={`₹${paise(q.data.totals.paid)}`} valueStyle={{ color: '#1c6442', fontSize: 18 }} /></Card>
        <Card size="small"><Statistic title="Owed" value={`₹${paise(q.data.totals.open)}`} valueStyle={{ color: q.data.totals.open > 0 ? '#b3382f' : '#1c6442', fontSize: 18 }} /></Card>
      </div>

      {q.data.totals.open > 0 && (
        <Alert
          type="info" showIcon
          message="Payments happen from your parent's app"
          description="M-12: you'll see receipts here once paid. Ask your guardian to open Fees on their app to complete the payment."
        />
      )}

      <Card size="small" title={`Invoices (${q.data.invoices.length})`}>
        {q.data.invoices.length === 0 ? (
          <Empty description="No invoices yet." style={{ padding: 20 }} />
        ) : (
          <Table<StudentFeeInvoice>
            size="small"
            pagination={false}
            rowKey="invoice_id"
            dataSource={q.data.invoices}
            columns={[
              { title: 'Head', dataIndex: 'fee_head_name', key: 'h' },
              { title: 'Amount', dataIndex: 'amount_paise', key: 'a', align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Paid', dataIndex: 'paid_paise', key: 'p', align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Due', dataIndex: 'due_on', key: 'd' },
              {
                title: 'Status', dataIndex: 'status', key: 's', width: 80,
                render: (v: string) => <Tag color={v === 'paid' ? 'success' : v === 'closed' ? 'default' : 'processing'}>{v}</Tag>,
              },
            ]}
          />
        )}
      </Card>

      {q.data.receipts.length > 0 && (
        <Card size="small" title={`Receipts (${q.data.receipts.length})`}>
          <Table
            size="small"
            pagination={false}
            rowKey="receipt_number"
            dataSource={q.data.receipts}
            columns={[
              { title: 'Receipt', dataIndex: 'receipt_number', key: 'n', render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{v}</span> },
              { title: 'Head', dataIndex: 'fee_head_name', key: 'h' },
              { title: 'Amount', dataIndex: 'amount_paise', key: 'a', align: 'right', render: (v: number) => `₹${paise(v)}` },
              { title: 'Method', dataIndex: 'method', key: 'm', render: (v: string) => <Tag>{v}</Tag> },
              { title: 'Date', dataIndex: 'received_on', key: 'd' },
            ]}
          />
        </Card>
      )}
    </Space>
  );
}

function paise(n: number): string { return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 }); }

const DOC_TONE: Record<StudentDocumentRow['kind'], string> = {
  receipt: 'blue', transfer_certificate: 'purple', bonafide: 'green', character: 'geekblue', custom: 'default',
};
const DOC_LABEL: Record<StudentDocumentRow['kind'], string> = {
  receipt: 'receipt', transfer_certificate: 'TC', bonafide: 'bonafide', character: 'character', custom: 'custom',
};

export function StudentDocumentsPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const q = useQuery({
    queryKey: ['student-documents', institution],
    queryFn: () => productApi.getStudentDocuments(institution),
    enabled: !!institution,
  });
  if (q.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  if (!q.data?.student) return <EmptyState />;
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>My documents</Typography.Title>
      {q.data.documents.length === 0 ? (
        <Empty description="No receipts or certificates yet." style={{ padding: 20 }} />
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {q.data.documents.map((d) => (
            <Card key={d.code + d.kind} size="small">
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                <Tag color={DOC_TONE[d.kind]}>{DOC_LABEL[d.kind]}</Tag>
                <div style={{ flex: 1 }}>
                  <b style={{ fontSize: 14 }}>{d.title}</b>
                  <div style={{ fontSize: 11, color: '#5b6478' }}>
                    <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{d.code}</span> · issued {d.issued_on}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </Space>
  );
}

/**
 * Student — Messages. School communication is guardian-primary (P-05, C-04).
 * Show a placeholder pointing students at the record their guardian sees.
 */
export function StudentMessagesPage() {
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>Messages</Typography.Title>
      <Alert
        type="info" showIcon
        message="School messages go to your guardian"
        description="P-05: the school reaches your parent/guardian first. If you have something urgent for a teacher, ask your guardian to open Messages on their app or raise it in class."
      />
    </Space>
  );
}

/** Single-homework detail. Reuses the student-homework list and filters. */
export function StudentAssignmentDetailPage() {
  const { institution = '', code = '' } = useParams<{ institution: string; code: string }>();
  const q = useQuery({
    queryKey: ['student-homework', institution],
    queryFn: () => productApi.getStudentHomework(institution),
    enabled: !!institution,
  });
  if (q.isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;
  const row = q.data?.rows.find((r) => r.id === code);
  if (!row) return <Empty description="Assignment not found." />;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>{row.title}</Typography.Title>
      <Card size="small">
        <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Tag>{row.subject_code}</Tag>
          <span style={{ fontSize: 12, color: '#5b6478' }}>
            {row.subject_name}{row.teacher_name ? ` · ${row.teacher_name}` : ''}
          </span>
          <div style={{ flex: 1 }} />
          <Tag color={row.my_status === 'graded' ? 'success' : row.my_status === 'submitted' ? 'processing' : row.my_status === 'missing' ? 'error' : 'default'}>
            {row.my_status}
          </Tag>
        </div>
        <div style={{ fontSize: 12.5, color: '#3f4a63', marginTop: 8 }}>
          Due <b>{row.due_on}</b>
        </div>
        {row.description && (
          <div style={{ marginTop: 12, fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{row.description}</div>
        )}
      </Card>

      <Card size="small" title="My submission">
        {row.my_status === 'pending' ? (
          <Alert type="warning" showIcon message="Not submitted yet — open the Homework tab to submit." />
        ) : (
          <>
            <div style={{ fontSize: 13 }}>Status: <Tag>{row.my_status}</Tag></div>
            {row.my_note && <div style={{ marginTop: 6, fontSize: 12.5, whiteSpace: 'pre-wrap' }}><b>Note:</b> {row.my_note}</div>}
            {row.my_grade_out_of_10 != null && (
              <div style={{ marginTop: 8, fontSize: 15 }}>Grade: <b>{row.my_grade_out_of_10}/10</b></div>
            )}
            {row.my_feedback && (
              <div style={{ marginTop: 6, fontSize: 12.5, color: '#553a9c' }}>Feedback: {row.my_feedback}</div>
            )}
          </>
        )}
      </Card>
    </Space>
  );
}
